import {Controller} from "@hotwired/stimulus"

import {get, post} from "@rails/request.js"
import {Calendar} from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import luxonPlugin from '@fullcalendar/luxon3'
import interactionPlugin from '@fullcalendar/interaction'

// This controller helps to preview a Image selected from a file input
export default class extends Controller {
  static targets = ['calendar', 'title', 'bulkActions', "cancelClassesModal",
    "cancelMultipleMessage", "cancelMultipleClassesButton",
    "selectedClassesCount"];
  static values = {
    timezone: String,
    gymId: Number,
    initialView: {type: String, default: "timeGridWeek"},
    canCancelMultipleClasses: {type: Boolean, default: false}
  };

  initialize() {
    console.debug("Calendar initialized", this.timezoneValue);

    this.selectMode = false;
    this.cancelableScheduleIds = [];

    if (this.canCancelMultipleClassesValue) {
      this.modal = bootstrap.Modal.getOrCreateInstance(this.cancelClassesModalTarget, {
        backdrop: 'static',
        keyboard: false
      });
    }

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, luxonPlugin, interactionPlugin],
      initialView: this.initialViewValue,
      themeSystem: 'bootstrap5',
      allDaySlot: false,
      height: '100%',

      // Fix this https://github.com/fullcalendar/fullcalendar/issues/6401
      locale: 'es-US', // This fix format 12 hrs at 12:00 PM

      timeZone: this.timezoneValue,
      nowIndicator: true,
      views: {
        timeGridWeek: {
          slotEventOverlap: true,
          // eventMaxStack: 2,
        },
        timeGridDay: {
          slotEventOverlap: false,
        }
      },
      eventTimeFormat: { // like '14:30:00'
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      },
      // selectable: true,
      // headerToolbar: {left: '', center: '', right: ''},
      // headerToolbar: {left: '', center: 'title', right: ''},
      // headerToolbar: {
      //   left: 'prev,next today',
      //   center: 'title',
      //   right: 'dayGridMonth,timeGridWeek,timeGridDay,list'
      // },
      dateClick: (info) => {
        const date = info.date.toJSON();
        const gymId = this.gymIdValue;
        console.debug('A day has been clicked!', info);
        console.info("Adding Schedule ", date, this.timezoneValue);

        get(`/calendars/schedules/new?gym_id=${gymId}&lesson_time=${date}`, {responseKind: "turbo_stream"});
      },
      eventClick: (info) => {
        console.debug(info);

        if (!this.canCancelMultipleClassesValue) {
          get(`/calendars/schedules/${info.event.id}`, {responseKind: "turbo_stream"});
          return;
        }

        const view = info.view;
        const validViews = view.type === 'timeGridWeek' || view.type === 'timeGridDay';

        if (!validViews) {
          get(`/calendars/schedules/${info.event.id}`, {responseKind: "turbo_stream"});
          return;
        }

        const event = info.event;

        console.debug("Clicked", event.id);
        // info.el.style.borderColor = 'red';

        const multipleKeyPressed = this.isMacPlatform() ? info.jsEvent.metaKey : info.jsEvent.ctrlKey;

        if (!multipleKeyPressed && !this.selectMode) {
          // Single selection
          get(`/calendars/schedules/${event.id}`, {responseKind: "turbo_stream"});
          return;
        }

        if (multipleKeyPressed) {
          console.log("Selecting multiple classes");
          this.enableBulkActions();
        }

        this._toggleCancelCheckbox(event.id, info.el);

      },
      eventDidMount: (info) => {
        if (!this.canCancelMultipleClassesValue) {
          return;
        }

        const view = info.view;
        const validViews = view.type === 'timeGridWeek' || view.type === 'timeGridDay';

        if (!validViews) {
          return;
        }

        // console.debug("Event mounted", info);
        const element = info.el;
        const scheduleId = info.event.id;

        const checkIcon = document.createElement('i');
        checkIcon.classList.add('event-cb', 'bi', 'bi-check-circle-fill', 'text-white', 'd-none');
        element.appendChild(checkIcon);

        const alreadySelected = this.cancelableScheduleIds.findIndex((id) => id === scheduleId) !== -1;

        if (alreadySelected) {
          checkIcon.classList.remove('d-none');
        } else {
          checkIcon.classList.add('d-none');
        }

      },
      events: (info, successCallback, failureCallback) => {
        this.loadEvents(info).then((schedules) => {
          const newData = schedules.map((schedule) => {
            return {
              id: schedule.id,
              start: schedule.lesson_time,
              end: schedule.end_time,
              title: schedule.lesson.name
            }
          })

          console.log("GOT", newData);

          successCallback(newData);
        }).catch(failureCallback);
      },
    })

    this.calendar.render();
    this._updateTitle();
  }

  isMacPlatform() {
    return navigator.platform.indexOf("Mac") === 0 || navigator.platform === "iPhone";
  }

  enableBulkActions() {
    this.selectMode = true;
    this.bulkActionsTarget.classList.remove('d-none');
  }

  disableBulkActions() {
    this.selectMode = false;
    this.bulkActionsTarget.classList.add('d-none');
  }

  reload() {
    console.debug("Calendar reload requested");
    this.calendar.refetchEvents();
  }

  connect() {
  }

  disconnect() {
  }


  /**
   *
   * @param scheduleId {string}
   * @param element {HTMLElement}
   * @private
   */
  _toggleCancelCheckbox(scheduleId, element) {
    const checkIcon = element.querySelector('.event-cb');
    const alreadySelected = this.cancelableScheduleIds.findIndex((id) => id === scheduleId) !== -1;

    if (alreadySelected) {
      checkIcon.classList.add('d-none');
      this.cancelableScheduleIds.splice(
        this.cancelableScheduleIds.findIndex((id) => id === scheduleId),
        1
      );
    } else {
      checkIcon.classList.remove('d-none');
      this.cancelableScheduleIds.push(scheduleId);
    }

    console.log("All checkboxes", this.cancelableScheduleIds);
    this.selectedClassesCountTarget.innerHTML = this.cancelableScheduleIds.length;

    if (this.cancelableScheduleIds.length === 0) {
      this.disableBulkActions();
    }
  }

  deselectAllClasses() {
    const checkBoxes = this.calendarTarget.querySelectorAll('.event-cb');

    for (const checkBox of checkBoxes) {
      checkBox.classList.add('d-none');
    }

    this.cancelableScheduleIds = [];
    this.disableBulkActions();
  }

  askBulkCancel() {
    this.cancelMultipleMessageTarget.innerHTML = `
      Deseas cancelar las ${this.cancelableScheduleIds.length} clases seleccionadas?
    `

    if (!this.modal.isOpened) {
      this.modal.show();
    }
  }

  cancelMultipleClasses(event) {
    console.warn("SEND =>", this.cancelableScheduleIds);

    this.cancelMultipleClassesButtonTarget.setAttribute("disabled", "disabled");
    this.cancelMultipleClassesButtonTarget.children[1].textContent = "Cancelando clases seleccionadas...";

    post(`/schedules/cancel_many`, {
        responseKind: "turbo_stream",
        body: {
          schedule_ids: this.cancelableScheduleIds,
        }
      }
    ).then(response => {
      console.debug("CANCEL MANY RESPONSE", response);

      this.cancelMultipleClassesButtonTarget.children[1].textContent = "Cancelar clases seleccionadas";
      this.cancelMultipleClassesButtonTarget.removeAttribute("disabled");

      if (response.ok) {
        this.deselectAllClasses();
        this.disableBulkActions();
        this.modal.hide();
        this.reload();
      }

      return response;
    })

  }

  loadEvents(info) {
    console.debug("Fetching events", info);
    return get("/calendars/schedules", {
      query: {gym_id: this.gymIdValue, from_date: info.start.toJSON(), to_date: info.end.toJSON()},
      responseKind: "json"
    }).then(response => {
      if (response.ok) {
        return response.json;
      } else {
        throw "Can't load data";
      }
    });
  }

  next() {
    this.calendar.next();
    this._updateTitle();
  }

  previous() {
    this.calendar.prev();
    this._updateTitle();
  }

  today() {
    this.calendar.today();
    this._updateTitle();
  }

  //dayGridMonth,timeGridWeek,timeGridDay,list

  changeToMonthView() {
    this.calendar.changeView('dayGridMonth');
    this._updateTitle();
  }

  changeToWeekView() {
    this.calendar.changeView('timeGridWeek');
    this._updateTitle();
  }

  changeToDayView() {
    this.calendar.changeView('timeGridDay');
    this._updateTitle();
  }

  changeToListView() {
    this.calendar.changeView('list');
    this._updateTitle();
  }

  _updateTitle() {
    this.titleTarget.innerHTML = this.calendar.view.title;
  }
}
